import type React from "react"
import { useTranslation } from "react-i18next"

import { FONT_WEIGHT } from "@/models/typography"
import { getDisplayValue } from "@/utils/value"
import Papa from "papaparse"

import {
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"

interface BillCsvPreviewProps {
  isError: boolean
  isLoading: boolean
  isTruncated: boolean
  previewText: string
}

const parseCsvRows = (csvText: string): string[][] => {
  const parsed = Papa.parse<string[]>(csvText, {
    skipEmptyLines: "greedy",
  })

  return parsed.data.map((row) => row.map((cell) => cell ?? ""))
}

const createOccurrenceKey = (
  rawKey: string,
  keyCounts: Map<string, number>
): string => {
  const count = keyCounts.get(rawKey) ?? 0
  keyCounts.set(rawKey, count + 1)

  return count === 0 ? rawKey : `${rawKey}__${count}`
}

const BillCsvPreviewSkeleton: React.FC = () => (
  <Stack sx={{ flex: 1, minHeight: 0, p: 2, pt: 0 }}>
    <Skeleton sx={{ flex: 1 }} variant="rounded" width="100%" />
  </Stack>
)

export const BillCsvPreview: React.FC<BillCsvPreviewProps> = ({
  isError,
  isLoading,
  isTruncated,
  previewText,
}) => {
  const { t } = useTranslation("sites")

  if (isLoading) {
    return <BillCsvPreviewSkeleton />
  }

  if (isError) {
    return (
      <Stack sx={{ gap: 1, p: 2, pt: 0 }}>
        <Typography color="error.main" role="status" variant="body2">
          {t("bills.detail.document.csv.error")}
        </Typography>
      </Stack>
    )
  }

  const csvRows = parseCsvRows(previewText)
  const headerRow = csvRows[0] ?? []
  const dataRows = csvRows.slice(1)
  const headerKeyCounts = new Map<string, number>()
  const headerKeys = headerRow.map((headerCell) =>
    createOccurrenceKey(`header-${headerCell}`, headerKeyCounts)
  )
  const rowKeyCounts = new Map<string, number>()
  const rowKeys = dataRows.map((csvRow) =>
    createOccurrenceKey(`row-${csvRow.join("\u0000")}`, rowKeyCounts)
  )

  return (
    <Stack sx={{ flex: 1, minHeight: 0, p: 2, pt: 0 }}>
      {headerRow.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {headerRow.map((headerCell, columnIndex) => (
                  <TableCell key={headerKeys[columnIndex]}>
                    <Typography sx={{ fontWeight: FONT_WEIGHT.bold }}>
                      {headerCell}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRows.map((csvRow, rowIndex) => (
                <TableRow
                  key={rowKeys[rowIndex]}
                  sx={{ "&:last-child td": { border: 0 } }}
                >
                  {headerRow.map((_, columnIndex) => (
                    <TableCell
                      key={`${rowKeys[rowIndex]}-${headerKeys[columnIndex]}`}
                    >
                      <Typography>
                        {getDisplayValue(csvRow[columnIndex])}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography color="text.secondary">
          {t("bills.detail.document.csv.empty")}
        </Typography>
      )}
      {isTruncated ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {t("bills.detail.document.csv.truncated")}
        </Typography>
      ) : null}
    </Stack>
  )
}
